import i18n from '@/locales'
import ResourceOverview from '@Reports/views/custom-report/templates/ResourceOverview.vue'
import BillOverview from '@Reports/views/custom-report/templates/BillOverview.vue'
import AccountBalance from '@Reports/views/custom-report/templates/AccountBalance.vue'
import ResourceMonitor from '@Reports/views/custom-report/templates/ResourceMonitor.vue'
import MonitorOverview from '@Reports/views/custom-report/templates/MonitorOverview.vue'
import Cover from '@Reports/views/custom-report/templates/Cover.vue'
import ResourceList from '@Reports/views/custom-report/templates/ResourceList.vue'
import MetricTable from '@Reports/views/custom-report/templates/MetricTable.vue'
import SuggestOverview from '@Reports/views/custom-report/templates/SuggestOverview.vue'
import { isScopedPolicyMenuHidden } from '@/utils/scopedPolicy'
import { hasPermission } from '@/utils/auth'

export const ELEMENT_TYPES = [
  {
    key: 'usage',
    label: i18n.t('report.element_types.usage'),
  },
  {
    key: 'metric',
    label: i18n.t('report.element_types.metric'),
  },
  {
    key: 'meter',
    label: i18n.t('report.element_types.meter'),
  },
  {
    key: 'metadata',
    label: i18n.t('report.element_types.metadata'),
  },
  {
    key: 'constant',
    label: i18n.t('report.element_types.constant'),
  },
  {
    key: 'attribute',
    label: i18n.t('report.element_types.attribute'),
  },
]
const getUsageLabel = (key) => {
  if (i18n.te(`usage['all.${key}']`)) {
    return i18n.t(`usage['all.${key}']`)
  }
  if (i18n.te(`usage['${key}']`)) {
    return i18n.t(`usage['${key}']`)
  }
  if (i18n.te(`k8s_usage['all.${key}']`)) {
    return i18n.t(`k8s_usage['all.${key}']`)
  }
  if (i18n.te(`k8s_usage['${key}']`)) {
    return i18n.t(`k8s_usage['all.${key}']`)
  }
  return key
}

const getMetricLabel = (key) => {
  return i18n.t(`report.metric['${key}']`)
}

// 'G', 'T', 'P', 'E', 'Z', 'Y'
const REPORT_ELEMENTS_UNIT = {
  b: 'B',
  kb: 'KB',
  mb: 'MB',
  gb: 'GB',
  tb: 'TB',
  pb: 'PB',
  he: i18n.t('common_60'),
  ge: i18n.t('common_61'),
  tai: i18n.t('common_62'),
  ke: i18n.t('common_63'),
}

/** 接口字段列表
 * value_type enum: int, float
 * service enum: compute, identity, image
 * unit_type enum: string, binary, percent
 */
export const REPORT_USAGES = [
  // service: compute
  {
    key: 'servers', label: getUsageLabel('servers'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'disks', label: getUsageLabel('disks'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'bucket_bytes', label: getUsageLabel('bucket_bytes'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.b, unit_type: 'binary',
  },
  {
    key: 'bucket_objects', label: getUsageLabel('bucket_objects'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'buckets', label: getUsageLabel('buckets'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'cpu_commit_rate.running', label: getUsageLabel('cpu_commit_rate.running'), service: 'compute', value_type: 'float', origin_unit: '%', unit_type: 'percent',
  },
  {
    key: 'disks.mounted', label: getUsageLabel('disks.mounted'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'disks.unmounted', label: getUsageLabel('disks.unmounted'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'disks.unready', label: getUsageLabel('disks.unready'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'eip', label: getUsageLabel('eip'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'eip.floating_ip', label: getUsageLabel('eip.floating_ip'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'eip.floating_ip.used', label: getUsageLabel('eip.floating_ip.used'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'eip.public_ip', label: getUsageLabel('eip.public_ip'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'eip.used', label: getUsageLabel('eip.used'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'memory_commit_rate.running', label: getUsageLabel('memory_commit_rate.running'), service: 'compute', value_type: 'float', origin_unit: '%', unit_type: 'percent',
  },
  {
    key: 'nics', label: getUsageLabel('nics'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'nics.guest', label: getUsageLabel('nics.guest'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'nics.host', label: getUsageLabel('nics.host'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'nics.lb', label: getUsageLabel('nics.lb'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'nics.reserve', label: getUsageLabel('nics.reserve'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'nics.guest.pending_delete', label: getUsageLabel('nics.guest.pending_delete'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'nics.netif', label: getUsageLabel('nics.netif'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'nics.eip', label: getUsageLabel('nics.eip'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'nics.db', label: getUsageLabel('nics.db'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'pending_delete_servers', label: getUsageLabel('pending_delete_servers'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'pending_delete_servers.cpu', label: getUsageLabel('pending_delete_servers.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'pending_delete_servers.disk', label: getUsageLabel('pending_delete_servers.disk'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'pending_delete_servers.ha', label: getUsageLabel('pending_delete_servers.ha'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'pending_delete_servers.ha.cpu', label: getUsageLabel('pending_delete_servers.ha.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'pending_delete_servers.ha.disk', label: getUsageLabel('pending_delete_servers.ha.disk'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'pending_delete_servers.ha.memory', label: getUsageLabel('pending_delete_servers.ha.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'pending_delete_servers.isolated_devices', label: getUsageLabel('pending_delete_servers.isolated_devices'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'pending_delete_servers.memory', label: getUsageLabel('pending_delete_servers.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'ports', label: getUsageLabel('ports'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'ports.eip', label: getUsageLabel('ports.eip'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'ports_exit', label: getUsageLabel('ports_exit'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'ports_exit.eip', label: getUsageLabel('ports_exit.eip'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'ready_servers', label: getUsageLabel('ready_servers'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'ready_servers.cpu', label: getUsageLabel('ready_servers.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'ready_servers.disk', label: getUsageLabel('ready_servers.disk'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'ready_servers.ha', label: getUsageLabel('ready_servers.ha'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'ready_servers.ha.cpu', label: getUsageLabel('ready_servers.ha.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'ready_servers.ha.disk', label: getUsageLabel('ready_servers.ha.disk'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'ready_servers.ha.memory', label: getUsageLabel('ready_servers.ha.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'ready_servers.isolated_devices', label: getUsageLabel('ready_servers.isolated_devices'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'ready_servers.memory', label: getUsageLabel('ready_servers.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'running_servers', label: getUsageLabel('running_servers'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'running_servers.cpu', label: getUsageLabel('running_servers.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'running_servers.disk', label: getUsageLabel('running_servers.disk'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'running_servers.ha', label: getUsageLabel('running_servers.ha'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'running_servers.ha.cpu', label: getUsageLabel('running_servers.ha.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'running_servers.ha.disk', label: getUsageLabel('running_servers.ha.disk'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'running_servers.ha.memory', label: getUsageLabel('running_servers.ha.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'running_servers.isolated_devices', label: getUsageLabel('running_servers.isolated_devices'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'running_servers.memory', label: getUsageLabel('running_servers.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'servers.cpu', label: getUsageLabel('servers.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'servers.disk', label: getUsageLabel('servers.disk'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'servers.ha', label: getUsageLabel('servers.ha'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'servers.ha.cpu', label: getUsageLabel('servers.ha.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'servers.ha.disk', label: getUsageLabel('servers.ha.disk'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'servers.ha.memory', label: getUsageLabel('servers.ha.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'servers.isolated_devices', label: getUsageLabel('servers.isolated_devices'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'servers.memory', label: getUsageLabel('servers.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'snapshot', label: getUsageLabel('snapshot'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'baremetals', label: getUsageLabel('baremetals'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'baremetals.cpu', label: getUsageLabel('baremetals.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'baremetals.memory', label: getUsageLabel('baremetals.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'enabled_hosts', label: getUsageLabel('enabled_hosts'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'enabled_hosts.cpu', label: getUsageLabel('enabled_hosts.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'enabled_hosts.cpu.virtual', label: getUsageLabel('enabled_hosts.cpu.virtual'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'enabled_hosts.memory', label: getUsageLabel('enabled_hosts.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'enabled_hosts.memory.virtual', label: getUsageLabel('enabled_hosts.memory.virtual'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'hosts', label: getUsageLabel('hosts'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'hosts.cpu', label: getUsageLabel('hosts.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'hosts.cpu.virtual', label: getUsageLabel('hosts.cpu.virtual'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'hosts.cpu.total', label: getUsageLabel('hosts.cpu.total'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'hosts.memory', label: getUsageLabel('hosts.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'hosts.memory.virtual', label: getUsageLabel('hosts.memory.virtual'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'hosts.memory.total', label: getUsageLabel('hosts.memory.total'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'isolated_devices', label: getUsageLabel('isolated_devices'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'networks', label: getUsageLabel('networks'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'storages', label: getUsageLabel('storages'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'storages.commit_rate', label: getUsageLabel('storages.commit_rate'), service: 'compute', value_type: 'float', origin_unit: '%', unit_type: 'percent',
  },
  {
    key: 'storages.virtual', label: getUsageLabel('storages.virtual'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'vpcs', label: getUsageLabel('vpcs'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'wires', label: getUsageLabel('wires'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'zones', label: getUsageLabel('zones'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'disks.count', label: getUsageLabel('disks.count'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'disks.mounted.count', label: getUsageLabel('disks.mounted.count'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'disks.unmounted.count', label: getUsageLabel('disks.unmounted.count'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'disks.unready.count', label: getUsageLabel('disks.unready.count'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'loadbalancer', label: getUsageLabel('loadbalancer'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'cache', label: getUsageLabel('cache'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'rds', label: getUsageLabel('rds'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'servers.system', label: getUsageLabel('servers.system'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'disks.system', label: getUsageLabel('disks.system'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'servers.system.cpu', label: getUsageLabel('servers.system.cpu'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'servers.system.memory', label: getUsageLabel('servers.system.memory'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'img.total.size', label: getUsageLabel('img.total.size'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.b, unit_type: 'binary',
  },
  {
    key: 'iso.total.size', label: getUsageLabel('iso.total.size'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.b, unit_type: 'binary',
  },
  {
    key: 'imgiso.total.size', label: getUsageLabel('imgiso.total.size'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.b, unit_type: 'binary',
  },
  {
    key: 'pending_delete_servers.last_week', label: getUsageLabel('pending_delete_servers.last_week'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'servers.last_week', label: getUsageLabel('servers.last_week'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'cluster.count', label: getUsageLabel('cluster.count'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'cluster.node.count', label: getUsageLabel('cluster.node.count'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'cluster.node.not_ready_count', label: getUsageLabel('cluster.node.not_ready_count'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'cluster.node.ready_count', label: getUsageLabel('cluster.node.ready_count'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.ge, unit_type: 'string',
  },
  {
    key: 'cluster.node.pod.capacity', label: getUsageLabel('cluster.node.pod.capacity'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'cluster.node.pod.count', label: getUsageLabel('cluster.node.pod.count'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'cluster.node.cpu.capacity', label: getUsageLabel('cluster.node.cpu.capacity'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'cluster.node.cpu.limit', label: getUsageLabel('cluster.node.cpu.limit'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'cluster.node.cpu.request', label: getUsageLabel('cluster.node.cpu.request'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'cluster.node.memory.capacity', label: getUsageLabel('cluster.node.memory.capacity'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'cluster.node.memory.limit', label: getUsageLabel('cluster.node.memory.limit'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
  {
    key: 'cluster.node.memory.request', label: getUsageLabel('cluster.node.memory.request'), service: 'compute', value_type: 'int', origin_unit: REPORT_ELEMENTS_UNIT.mb, unit_type: 'binary',
  },
]

// 监控字段
export const REPORT_METRICS = [
  {
    key: 'vm_cpu.usage_active',
    label: i18n.t('compute.text_523'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'vm_mem.used_percent',
    label: i18n.t('compute.text_518'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'vm_diskio.read_bps',
    label: i18n.t('compute.text_526'),
    value_type: 'int',
    origin_unit: 'bps',
    unit_type: 'bps',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'vm_diskio.write_bps',
    label: i18n.t('compute.text_527'),
    value_type: 'int',
    origin_unit: 'bps',
    unit_type: 'bps',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'vm_diskio.read_iops',
    label: getMetricLabel('vm_diskio.read_iops'),
    value_type: 'int',
    origin_unit: REPORT_ELEMENTS_UNIT.ci,
    unit_type: 'string',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'vm_diskio.write_iops',
    label: getMetricLabel('vm_diskio.write_iops'),
    value_type: 'int',
    origin_unit: REPORT_ELEMENTS_UNIT.ci,
    unit_type: 'string',
    period: 5, // 监控周期 单位小时
  },
  // {
  //   key: 'vm_diskio.read_bytes',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  // {
  //   key: 'vm_diskio.write_bytes',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  {
    key: 'vm_disk.used_percent',
    label: i18n.t('compute.text_533'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'vm_netio.bps_recv',
    label: i18n.t('compute.text_524'),
    value_type: 'int',
    origin_unit: 'bps',
    unit_type: 'bps',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'vm_netio.bps_sent',
    label: i18n.t('compute.text_525'),
    value_type: 'int',
    origin_unit: 'bps',
    unit_type: 'bps',
    period: 5, // 监控周期 单位小时
  },
  // {
  //   key: 'oss_netio.bps_recv',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  // {
  //   key: 'oss_netio.bps_sent',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  {
    key: 'rds_conn.used_percent',
    label: i18n.t('db.text_18'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'rds_cpu.usage_active',
    label: i18n.t('db.text_11'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'rds_mem.used_percent',
    label: i18n.t('db.text_12'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'rds_netio.bps_recv',
    label: i18n.t('db.text_19'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'rds_disk.used_percent',
    label: i18n.t('db.text_20'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'rds_conn.active_count',
    label: i18n.t('db.text_386'),
    value_type: 'int',
    origin_unit: '',
    unit_type: '',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'rds_conn.failed_count',
    label: i18n.t('db.text_387'),
    value_type: 'int',
    origin_unit: '',
    unit_type: '',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'agent_cpu.usage_active',
    label: 'Agent ' + i18n.t('compute.text_523'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'agent_disk.used_percent',
    label: 'Agent ' + i18n.t('compute.text_533'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'agent_diskio.read_bps',
    label: 'Agent ' + i18n.t('compute.text_526'),
    value_type: 'int',
    origin_unit: 'bps',
    unit_type: 'bps',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'agent_diskio.write_bps',
    label: 'Agent ' + i18n.t('compute.text_527'),
    value_type: 'float',
    origin_unit: '',
    unit_type: '',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'agent_diskio.read_iops',
    label: 'Agent ' + getMetricLabel('vm_diskio.read_iops'),
    value_type: 'float',
    origin_unit: '',
    unit_type: '',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'agent_diskio.write_iops',
    label: 'Agent ' + getMetricLabel('vm_diskio.write_iops'),
    value_type: 'float',
    origin_unit: '',
    unit_type: '',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'agent_diskio.reads',
    label: 'Agent ' + i18n.t('monitor.text_129'),
    value_type: 'int',
    origin_unit: '',
    unit_type: '',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'agent_diskio.writes',
    label: 'Agent ' + i18n.t('monitor.text_130'),
    value_type: 'int',
    origin_unit: '',
    unit_type: '',
    period: 5, // 监控周期 单位小时
  },
  // {
  //   key: 'agent_diskio.read_bytes',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  // {
  //   key: 'agent_diskio.write_bytes',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  {
    key: 'agent_mem.used_percent',
    label: 'Agent ' + i18n.t('compute.text_518'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'agent_net.bps_sent',
    label: 'Agent ' + i18n.t('compute.text_525'),
    value_type: 'int',
    origin_unit: 'bps',
    unit_type: 'bps',
    period: 5, // 监控周期 单位小时
  },
  {
    key: 'agent_net.bps_recv',
    label: 'Agent ' + i18n.t('compute.text_524'),
    value_type: 'int',
    origin_unit: 'bps',
    unit_type: 'bps',
    period: 5, // 监控周期 单位小时
  },
  // {
  //   key: 'agent_net.bytes_sent',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  // {
  //   key: 'agent_net.bytes_recv',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  {
    key: 'storage.usage_active',
    label: i18n.t('storage.usage_active'),
    value_type: 'float',
    origin_unit: '%',
    unit_type: 'percent',
    period: 5, // 监控周期 单位小时
  },
  // {
  //   key: 'storage.free',
  //   label: i18n.t('sto'),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  // {
  //   key: 'redis.used_memory',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  // {
  //   key: 'redis.used_memory_peak',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  // {
  //   key: 'redis.used_cpu_sys',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
  // {
  //   key: 'redis.used_cpu_user',
  //   label: i18n.t(''),
  //   value_type: 'float',
  //   origin_unit: '',
  //   unit_type: '',
  //   period: 5, // 监控周期 单位小时
  // },
]

export const PERIOD_UNIT_OPTS = [
  {
    id: 'hour',
    label: i18n.t('report.time_range_unit.hour'),
  },
  {
    id: 'day',
    label: i18n.t('report.time_range_unit.day'),
  },
  {
    id: 'month',
    label: i18n.t('report.time_range_unit.month'),
  },
  {
    id: 'year',
    label: i18n.t('report.time_range_unit.year'),
  },
]

export const ATTRIBUTE_KEY_OPTS = [
  {
    id: 'name',
    name: i18n.t('common_151'),
  },
  {
    id: 'ips',
    name: 'IP',
  },
  {
    id: 'status',
    name: i18n.t('scope.text_426'),
  },
  {
    id: 'vcpu_count',
    name: 'CPU',
    value_type: 'int',
  },
  {
    id: 'vmem_size',
    name: i18n.t('common_236') + '(G)',
    value_type: 'int',
    calc: 'x / 1024',
  },
  {
    id: 'disk',
    name: i18n.t('dictionary.storage') + '(G)',
    value_type: 'int',
    calc: 'x / 1024',
  },
  {
    id: 'os_type',
    name: i18n.t('compute.text_267'),
  },
  {
    id: 'hostname',
    name: i18n.t('common_388'),
  },
  {
    id: 'host',
    name: i18n.t('dictionary.host'),
  },
  {
    id: 'tenant',
    name: i18n.t('dictionary.project'),
  },
  {
    id: 'region',
    name: i18n.t('compute.text_177'),
  },
  {
    id: 'created_at',
    name: i18n.t('compute.text_243'),
  },
]

export const REPORT_TEMPLATES = {
  Cover: {
    label: i18n.t('report.template_type.cover'),
    type: 'Cover',
    component: Cover,
  },
  ResourceOverview: {
    label: i18n.t('report.template_type.resource_overview'),
    type: 'ResourceOverview',
    component: ResourceOverview,
  },
  BillOverview: {
    label: i18n.t('report.template_type.bill_overview'),
    type: 'BillOverview',
    component: BillOverview,
  },
  AccountBalance: {
    label: i18n.t('report.template_type.account_balance'),
    type: 'AccountBalance',
    component: AccountBalance,
  },
  ResourceMonitor: {
    label: i18n.t('report.template_type.resource_monitor'),
    type: 'ResourceMonitor',
    component: ResourceMonitor,
  },
  MonitorOverview: {
    label: i18n.t('report.template_type.monitor_overview'),
    type: 'MonitorOverview',
    component: MonitorOverview,
  },
  ResourceList: {
    label: i18n.t('report.template_type.resource_list'),
    type: 'ResourceList',
    component: ResourceList,
  },
  MetricTable: {
    label: i18n.t('report.template_type.metric_table'),
    type: 'MetricTable',
    component: MetricTable,
  },
  SuggestOverview: {
    label: i18n.t('report.template_type.suggest_overview'),
    type: 'SuggestOverview',
    component: SuggestOverview,
  },
}

const hasWorkflowPermission = (vm) => {
  return vm.$store.getters.workflow.enabledKeys?.length > 0 && !!((vm.$store.getters.userInfo.services || []).find(v => v.type === 'itsm' && v.status === true)) && !isScopedPolicyMenuHidden('navbar_hidden_items.workflow')
}

export const getResourceMaps = (vm) => {
  return {
    meProcess: {
      menu_key: 'me-process',
      res_name: 'historic-process-instances',
      label: i18n.t('common_198') + ' - ' + i18n.t('common_412'),
      id: 'meProcess',
      component: 'MeProcessList',
      hasPermission: hasWorkflowPermission(vm),
      params: {
        scope: vm.$store.getters.scope,
        limit: 20,
        details: true,
      },
      resData: {},
    },
    approvalStartProcess: {
      menu_key: 'approval-start-process',
      res_name: 'process-tasks',
      label: i18n.t('common_198') + ' - ' + i18n.t('common_203'),
      id: 'approvalStartProcess',
      component: 'ApprovalStartProcessList',
      hasPermission: hasWorkflowPermission(vm),
      params: {
        scope: vm.$store.getters.scope,
        limit: 20,
        details: true,
      },
      resData: {},
    },
    approvedDoneProcess: {
      menu_key: 'approved-done-process',
      res_name: 'historic-task-instances',
      label: i18n.t('common_198') + ' - ' + i18n.t('common_413'),
      id: 'approvedDoneProcess',
      component: 'ApprovedDoneProcessList',
      hasPermission: hasWorkflowPermission(vm),
      params: {
        scope: vm.$store.getters.scope,
        limit: 20,
        details: true,
      },
      resData: {},
    },
    mePartakeProcess: {
      menu_key: 'me-partake-process',
      res_name: 'historic-task-instances',
      label: i18n.t('common_198') + ' - ' + i18n.t('common_414'),
      id: 'mePartakeProcess',
      component: 'MePartakeProcessList',
      hasPermission: hasWorkflowPermission(vm),
      params: {
        scope: vm.$store.getters.scope,
        limit: 20,
        details: true,
      },
      resData: {},
    },
    allProcess: {
      menu_key: 'all-process',
      res_name: 'historic-process-instances',
      label: i18n.t('common_198') + ' - ' + i18n.t('common_737'),
      id: 'allProcess',
      component: 'AllProcessList',
      hasPermission: hasWorkflowPermission(vm),
      params: {
        scope: vm.$store.getters.scope,
        limit: 20,
        details: true,
      },
      resData: {},
    },
    billOriginResType: {
      menu_key: 'bill-origin-res-type',
      res_name: 'billsanalysises',
      label: i18n.t('bill.origin_res_type_cost'),
      id: 'billOriginResType',
      component: 'BillOriginResTypeList',
      hasPermission: hasPermission({ key: 'bill_analysises_list' }),
      params: {
        scope: vm.$store.getters.scope,
        limit: 20,
        details: true,
      },
      resData: {},
    },
    analysisAlarm: {
      menu_key: 'analysis-alarm',
      res_name: 'bill_analysis_alarms',
      label: i18n.t('bill.analysis_cost_alarm'),
      id: 'analysisAlarm',
      component: 'AnalysisAlarmList',
      hasPermission: hasPermission({ key: 'bill_analysis_alarms_list' }),
      params: {
        scope: vm.$store.getters.scope,
        limit: 20,
        details: true,
      },
      resData: {},
    },
    analysisAlarmHistory: {
      menu_key: 'analysis-alarm-history',
      res_name: 'bill_analysis_alarm_records',
      label: i18n.t('bill.analysis_cost_alarm_history'),
      id: 'analysisAlarmHistory',
      component: 'AnalysisAlarmHistoryList',
      hasPermission: hasPermission({ key: 'bill_analysis_alarm_records_list' }),
      params: {
        scope: vm.$store.getters.scope,
        limit: 20,
        details: true,
      },
      resData: {},
    },
  }
}
